import {observable, action, computed, toJS} from 'mobx';

import { registrationStore, catalogStore, profileStore, vehicleInformationStore, usersStore, translationsStore, installerProvidersStore } from '../../';

import { api } from '../../../services';

import { uniqWith, flatten } from 'ramda';

class Step_3 {
  @observable
  inProgress = false;
  @observable
  serviceTypes;
  @observable
  linkId;
  @observable
  heartbeat = {
    status: 0
  };
  @observable
  servicesToggledChanges = false;
  @observable
  customServicesCache = {};

  @observable
  servicing = {
    isServicingCheckBoxEnabled: false,
    hasManyModels: false,
    models: [],
    variants: []
  };

  @observable
  loadingServicing = false;
  @observable
  loadingActiveInsurancePolicy = false;
  @observable
  loadingCatalog = true;

  @observable
  activeKey = '1';

  @observable
  selectedAdditionalDevices;

  constructor(catalogStore, profileStore, registrationStore, vehicleInformationStore, usersStore, translationStore, installerProvidersStore) {
    this.catalogStore = catalogStore;
    this.profileStore = profileStore;
    this.registrationStore = registrationStore;
    this.vehicleInformationStore = vehicleInformationStore;
    this.usersStore = usersStore;
    this.translationsStore = translationsStore;
    this.installerProvidersStore = installerProvidersStore;
  }

  @action
  setCurrentTab(activeKey) {
    this.activeKey = activeKey;
  }

  @action
  loadActiveInsurancePolicy() {
    this.loadingActiveInsurancePolicy = true;
    return api({ action: 'get active insurance policy' }, this.registrationStore.plate)
      .catch(err => {
        return null;
      })
      .finally(() => {
        this.loadingActiveInsurancePolicy = false;
      });
  }

  @observable
  assetServiceLevel = null;

  @observable
  loadingAssetServiceLevel = false;

  @action
  loadAssetServiceLevel() {
    this.loadingAssetServiceLevel = true;
    return api({ action: 'get asset service level' }, this.registrationStore.user.assets[0].imei)
      .then(res => {
        this.assetServiceLevel = res.asset.service_level;
      })
      .catch(err => {
        this.assetServiceLevel = null;
      })
      .finally(() => {
        this.loadingAssetServiceLevel = false;
      });
  }

  @action
  loadServicing() {
    if (this.registrationStore.plate) {
      this.loadingServicing = true;
      return api({ action: 'get serviceTypes' }, this.registrationStore.plate)
        .then(serviceTypes => {
          this.serviceTypes = serviceTypes;
          this.servicing.isServicingCheckBoxEnabled = this.serviceTypes.Successo && serviceTypes.CodiceErrore == 0;
          if (this.servicing.isServicingCheckBoxEnabled) {
            const modelli = this.serviceTypes.modelli.map(modello => ({
              ...modello,
              tagliandi: modello.tagliandi.map(tagliando => ({ ...tagliando, DescrizioneModello: modello.NomeModello }))
            }));
            this.servicing.variants = uniqWith(t => t.LinkId)(...modelli.map(m => m.tagliandi)).map(t => ({
              LinkId: t.LinkId,
              MidId: t.MidId,
              Desc: `${t.DescrizioneVariante || t.DescrizioneModello ? t.DescrizioneModello + ' (' + t.LinkId + ')' : t.LinkId}`
            }));
          }
        })
        .catch(error => {
          return { error };
        })
        .then(() => {
          this.loadingServicing = false;
        });
    }
    return Promise.reject('no plate');
  }

  loadMaintenancesForm() {
    if (this.registrationStore.maintenances) {
      const { linkId, custom } = this.registrationStore.maintenances;
      if (!!linkId) {
        this.setFormField('maintenances.servicing.checked', true);
        this.setFormField('maintenances.servicing.variant', linkId);
      }
      if (custom && custom.length > 0) {
        this.setFormField('maintenances.customServices.checked', true);
        let customServices = [];
        custom.forEach(c => customServices.push(c));
        this.setFormField('maintenances.customServices.services', custom);
      }
    }
  }

  @action
  async submit() {
    this.inProgress = true;
    //check controllo personalData
    const { serviceId, paymentType: pageId, paymentExceptionForActivation, externalPayment, ghostInstallationLocation } = this.formValues.product;
    const additionalDevices = this.selectedAdditionalDevices;
    const service = this.services.find(s => s.id === serviceId);

    const commonPaymentType = this.paymentMethodsByServiceId(serviceId).find(p => p.pageId === pageId);
    const { risk, service_level, productId: zuoraChannelId, skipInvoice, oneTime, autoRenew, documentType } = service;

    let subscription = Object.assign({}, registrationStore.subscription, { zuoraChannelId, skipInvoice });

    if (oneTime) {
      const { ratePlanId: id, name, description, duration, renew, productRatePlanCharges } = service.oneTime;
      const zuoraOneTimeRatePlan = {
        id,
        name,
        description,
        service_level,
        risk,
        duration,
        renew,
        productRatePlanCharges,
        paymentType: paymentExceptionForActivation ? this.getPaymentByPageId(externalPayment) : commonPaymentType,
        autoRenew,
        documentType
      };
      Object.assign(subscription, { zuoraOneTimeRatePlan });
    }

    const { startDate: decurringTime, authorizedAssistanceCenter: installerInformation, signaturePlace, signatureDate, signatureMethod, dealerDigitalSignatureEmail } = this.formValues.product;
    const { agreementProfiling, agreementMarketing, agreementThirdParties } = this.formValues.personalData;

    Object.assign(subscription, { decurringTime, signatureDate, installerInformation, signaturePlace, signatureMethod, dealerDigitalSignatureEmail, agreementProfiling, agreementMarketing, agreementThirdParties });

    //maintenances
    const { servicing, customServices } = this.formValues.maintenances;
    let maintenances = {
      linkId: false,
      custom: []
    };
    if (servicing.checked) {
      maintenances = { ...maintenances, linkId: servicing.variant };
    }
    if (customServices.checked) {
      maintenances = { ...maintenances, custom: customServices.services };
    }

    return api({ action: 'update asset' }, this.registrationStore.session, this.registrationStore.assetId, {
      maintenances,
      additionalDevices,
      ghostInstallationLocation,
      subscription: Object.assign({}, subscription, {
        status: this.registrationStore.subscription.status > 4 ? this.registrationStore.subscription.status : 4
      })
    })
      .then(updatedAsset => {
        this.registrationStore.setUserAsset(updatedAsset);
        this.usersStore.addOrModify(this.registrationStore.user);
        this.registrationStore.setSession(this.registrationStore.user._id);
        return {};
      })
      .catch(error => {
        return { error };
      })
      .then(({ error }) => {
        this.inProgress = false;
        //
        const { session } = this.registrationStore;
        let cache = this.customServicesCache;
        delete cache[session];
        this.customServicesCache = Object.assign({}, cache);
        if (error) {
          throw error;
        }
        return {};
      });
  }

  @action
  async checkGhostDevices() {

    const additionalDevices = this.selectedAdditionalDevices;

    if (!additionalDevices || additionalDevices.length === 0) {
      return { isValid: true, invalidDevices: [] };
    }

    try {
      const userId = this.registrationStore.user._id;
      const checkPromises = additionalDevices.map(device => {
        return api({ action: 'check ghost device' }, userId, device.imei || device)
          .then(result => ({
            imei: device.imei || device,
            valid: !!result && Object.keys(result).length > 0
          }))
          .catch(() => ({
            imei: device.imei || device,
            valid: false
          }));
      });

      const results = await Promise.all(checkPromises);
      const invalidDevices = results.filter(result => !result.valid);

      return {
        isValid: invalidDevices.length === 0,
        invalidDevices
      };
    } catch (error) {
      console.error('Error checking ghost devices:', error);
      return {
        isValid: false,
        invalidDevices: [],
        error
      };
    }
  }

  @computed
  get formValues() {
    return registrationStore.formValues.step_3;
  }

  @action
  setMyPosition() {
    this.registrationStore.setPosition(3);
  }

  @computed
  get session() {
    return this.registrationStore.session;
  }

  @action
  setFormField(field, value) {
    this.registrationStore.setFormField('step_3', field, value);
  }

  filterServicesByAssetServiceLevel(services) {
    const filtered = services.filter(service => !this.assetServiceLevel || this.assetServiceLevel === service.service_level);
    if (filtered.length > 0) {
      return filtered;
    } else {
      return services;
    }
  }

  @computed
  get services() {
    let services = [];
    this.catalogStore.oneTimes.forEach(oneTime => services.push(this.buildService(oneTime.id)));
    return this.filterServicesByAssetServiceLevel(services)
      .filter(service => this.isSameRiskOfVehicle(service))
      .filter(service => this.isSameTypeOfVehicle(service))
      .sort((s1, s2) => {
        if (s1.productName < s2.productName) {
          return -1;
        } else if (s1.productName > s2.productName) {
          return 1;
        } else {
          return s1.name < s2.name ? -1 : 1;
        }
      });
  }

  @action
  getChargeModelByServiceId(serviceId) {
    const product = this.catalogStore.oneTimes.find(service => service.id === serviceId);
    return product && product.chargeModel;
  }

  @computed
  get user() {
    try {
      return this.registrationStore.user;
    } catch (e) {
      return null;
    }
  }

  @computed
  get customServices() {
    return this.formValues.maintenances.customServices;
  }

  @action
  addCustomService(service) {
    let services = this.formValues.maintenances.customServices.services || [];
    services = services.filter(service => !service._draft);
    this.setFormField('maintenances.customServices.services', [...services, service]);
  }

  @action
  draftCustomService(index, service) {
    if (index >= 0 && index <= this.formValues.maintenances.customServices.services.length + 1) {
      let { services } = this.formValues.maintenances.customServices;
      service = { ...service, _draft: true };
      this.setFormField('maintenances.customServices.services', [...services.slice(0, index), service, ...services.slice(index + 1)]);
    }
  }

  @action
  removeCustomService(index) {
    if (index >= 0 && index <= this.formValues.maintenances.customServices.services.length + 1) {
      const { services } = this.formValues.maintenances.customServices;
      this.setFormField('maintenances.customServices.services', [...services.slice(0, index), ...services.slice(index + 1)]);
    }
  }

  @action
  confirmDraftsCustomService() {
    let services = this.formValues.maintenances.customServices.services || [];
    services = services.map(service => {
      delete service['_draft'];
      return service;
    });
    this.servicesToggledChanges = !this.servicesToggledChanges;
    this.setFormField('maintenances.customServices.services', [...services]);
    //
    const { session } = this.registrationStore;
    this.customServicesCache = Object.assign({}, this.customServicesCache, { [session]: services });
  }

  @computed
  get hasMaintenances() {
    const { maintenances } = this.registrationStore.user.assets && this.registrationStore.user.assets[0];
    try {
      return maintenances && (maintenances.linkId || (maintenances.custom && maintenances.custom.length > 0));
    } catch (e) {
      return false;
    }
  }

  paymentMethodsByServiceId(serviceId) {
    return serviceId
      ? this.profileStore.paymentsMethods.map(el => {
        const stripeV2DealerPaymentNameToTransform = el.pageName && el.pageName.match(/stripe-v2-dealer(.*)/);
        if (stripeV2DealerPaymentNameToTransform) {
          const environmentName = stripeV2DealerPaymentNameToTransform && Array.isArray(stripeV2DealerPaymentNameToTransform) && stripeV2DealerPaymentNameToTransform.length > 1 && stripeV2DealerPaymentNameToTransform[1];
          el.pageName = `${this.translationsStore.current["shared"]["lblCreditCard"]}${ environmentName ? ` (${environmentName.substring(1)})` : ''}`;
        }
        return el;
      })
      : [];
  }

  allowedPaymentMethodsByServiceId(serviceId) {
    if (serviceId) {
      const paymentsMethods = this.paymentMethodsByServiceId(serviceId);

      const productChargeModel = this.getChargeModelByServiceId(serviceId);
      const productRequireBillablePaymentMethod = productChargeModel === 'monthly';

      if (productRequireBillablePaymentMethod) {

        return paymentsMethods.filter(paymentMethod => ['SEPA', 'Credit Card'].includes(paymentMethod.pageType));
      } else {

        return paymentsMethods;
      }
    } else {

      return [];
    }
  }

  otherPaymentType() {
    return this.profileStore.paymentsMethods.find(p => p.pageId == 'other');
  }

  getPaymentByPageId(pageId) {
    return this.profileStore.paymentsMethods.find(p => p.pageId == pageId);
  }

  externalPayments() {
    const pageIdExternal = ['other', 'banktransfer', 'cash', 'collectedbydealer', 'collectedbyvalandro'];

    return this.profileStore.paymentsMethods.filter(p => pageIdExternal.includes(p.pageId));
  }

  isSameLevelOfAsset(service) {}

  isSameRiskOfVehicle(service) {
    if (!this.registrationStore.car) {
      return false;
    }
    if (!service.risk) {
      return true;
    }
    let {
      make: { codiceMarca }
    } = this.registrationStore.car;
    let risk = this.vehicleInformationStore.riskPerMaker.find(mRisk => parseInt(mRisk.codice) === parseInt(codiceMarca));
    risk = risk || { type: 'carbeat_protection_lv', risk: 'low' };
    return risk.risk === service.risk;
  }

  isSameTypeOfVehicle(service) {
    if (!this.registrationStore.car) {
      return false;
    }

    const vehicleType = registrationStore.vehicleType ? registrationStore.vehicleType : 'car';
    const serviceVehicleType = this.vehicleInformationStore.getVehicleTypeByAlias(service && service.vehicleType);

    if (serviceVehicleType) {
      return serviceVehicleType === vehicleType;
    } else {
      return vehicleType === 'car';
    }
  }

  buildService(buildId, { oneTimeProduct = true } = { oneTimeProduct: true }) {
    if (oneTimeProduct) {
      let oneTime = this.catalogStore.oneTimes.find(o => o.id === buildId);
      oneTime = Object.assign({}, oneTime, { ratePlanId: oneTime.id });
      delete oneTime.id;

      const { name, description, risk, service_level, productId, productName, skipInvoice, vehicleType, autoRenew, documentType } = oneTime;

      ['risk', 'service_level', 'productId', 'productName'].forEach(field => delete oneTime[field]);

      delete oneTime.risk;
      delete oneTime.skipInvoice;
      const id = oneTime.ratePlanId;
      return {
        id,
        name,
        description,
        risk,
        service_level,
        productId,
        productName,
        oneTime,
        vehicleType,
        skipInvoice,
        autoRenew,
        documentType
      };
    }
  }

  isServiceValid = service => {
    return this.isServiceValidDescription(service) && (this.isServiceValidKm(service) || this.isServiceValidDate(service));
  };

  isServiceValidDescription = service => {
    const { description } = service;
    return description && description.length > 0 && description.length < 201;
  };

  isServiceValidKm = service => {
    const { km } = service;
    return !!km;
  };

  isServiceValidDate = service => {
    const { date } = service;
    return !!date;
  };
}

export default new Step_3(catalogStore, profileStore, registrationStore, vehicleInformationStore, usersStore, translationsStore, installerProvidersStore);



// WEBPACK FOOTER //
// ./src/stores/registrationStore/steps/step_3.js